/**
 * TCS departments, department roles & staff assignments (PE-2147, backend PE-2146).
 *
 * Mirrors watchman's `departments` domain, reached through osprey-api's
 * `/iam/*` catch-all (`/iam/departments` → watchman `/api/v1/departments`).
 * Every route is gated on watchman's DEPARTMENT resource: READ for lists,
 * CREATE/UPDATE for departments and roles, MANAGE for the permissions matrix
 * and staff assignments. Only COLLECTIVE-scope users are assignable.
 */

// NB: `erasableSyntaxOnly` — const objects + unions, never TS enums.

export interface Department {
  id: string;
  name: string;
  description: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface DepartmentRole {
  id: string;
  departmentId: string;
  name: string;
  description: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateDepartmentPayload {
  name: string;
  description?: string;
}
export type UpdateDepartmentPayload = Partial<CreateDepartmentPayload>;

export interface CreateDepartmentRolePayload extends CreateDepartmentPayload {
  departmentId: string;
}
export type UpdateDepartmentRolePayload = Partial<CreateDepartmentPayload>;

/** Watchman `Resource` enum (prisma/schema/permission.model.prisma). */
export const RESOURCES = [
  'MERCHANT',
  'INDUSTRY',
  'BUSINESS_CATEGORY',
  'USER',
  'ROLE',
  'PERMISSION',
  'PRODUCT',
  'CATEGORY',
  'COLOUR',
  'SIZE',
  'SIZE_GUIDE',
  'MATERIAL',
  'ORDER',
  'INVENTORY',
  'CART',
  'PAYMENT',
  'DHL',
  'DASHBOARD',
  'AUDIT',
  'EMAIL_CAMPAIGN',
  'COLLECTIVE_REVENUE',
  'API_KEY',
  'CONTACT',
  'CUSTOM_BESPOKE',
  'NEWSLETTER',
  'SEARCH',
  'SOLUTION',
  'APP_CLIENT',
  'PAYMENT_DISPUTE',
  'RECONCILIATION',
  'SETTLEMENT',
  'PAYOUT_DESTINATION',
  'INVOICE',
  'TENANT',
  'CHARTER',
  'PORTFOLIO',
  'MERCHANT_ONBOARDING',
  'OBSERVABILITY',
  'SALES_PRICING',
  'REPORTING',
  'BRANCH',
  'DEPARTMENT',
  'INTELLIGENCE_REPORT',
  'SALES_CONTACT',
  'FEATURE_FLAG',
  'CONSENT',
] as const;
export type Resource = (typeof RESOURCES)[number];

/** Watchman `ResourceAction` enum. */
export const RESOURCE_ACTIONS = [
  'CREATE',
  'READ',
  'UPDATE',
  'DELETE',
  'MANAGE',
  'DEACTIVATE',
  'FULFIL',
  'REFUND',
  'GRANT',
  'REVOKE',
  'MANAGE_DEFAULTS',
] as const;
export type ResourceAction = (typeof RESOURCE_ACTIONS)[number];

/** `Title Case` label for an UPPER_SNAKE enum value. */
export const enumLabel = (value: string): string =>
  value
    .toLowerCase()
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');

/** One row of `GET/PUT /department-roles/:id/permissions`. */
export interface RolePermissionEntry {
  resource: Resource;
  actions: ResourceAction[];
}

export interface StaffAssignment {
  id: string;
  userId: string;
  departmentId: string;
  departmentRoleId: string;
  createdAt: string;
  department?: Department;
  departmentRole?: DepartmentRole;
}

export interface CreateStaffAssignmentPayload {
  userId: string;
  departmentId: string;
  departmentRoleId: string;
}
